import { Currency } from '@ps/web-core-enums';
import React from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import type { Error } from '@swf/core/utils/validators';

import type { MaxLimitData, Rate } from '../types/Cashier';
import { DaysLimitHitTypes, DepositLimitHitTypes } from '../types/Cashier';
import { convertAmountByRate, getConversionRate, isZeroExponentCurrency } from '../utils/cashier';

import {
  TXTWCL_Please_choose_another_payment__ELL,
  TXTWCL_SorryX_your_deposit_request_ha_ELL,
  TXTWCL_This_is_because_the_maximum_li_ELL,
  TXTWCL_This_is_because_the_maximum_li_ELL_1,
  TXTWCL_This_is_because_the_maximum_li_ELL_2,
  TXTWCL_This_is_because_you_can_only_d_ELL,
  TXTWCL_This_is_because_you_can_only_d_ELL_1,
  TXTWCL_This_is_because_you_can_only_d_ELL_2,
  TXTWCL_You_can_deposit_up_to_N0_befor_ELL,
  TXTWCL_You_can_deposit_up_to_N0_befor_ELL_1,
  TXTWCL_You_can_only_deposit_another_N_ELL,
  TXTWCL_You_have_N0_left_on_your_Staff_ELL,
  TXTWCL_You_have_now_reached_this_limi_ELL,
  TXTWCL_Your_deposit_of_N0_is_more_tha_ELL,
  TXTWCL__Please_enter_a_lower_amountX
} from './maxLimit.messages.json';

const depositLimitMessages = {
  [DepositLimitHitTypes.TempAccountLimit]: TXTWCL_You_can_deposit_up_to_N0_befor_ELL,
  [DepositLimitHitTypes.StaffLimit]: TXTWCL_You_have_N0_left_on_your_Staff_ELL
};

const setLimitsMessage = (remainingLimit: number, daysLimitHitType: DaysLimitHitTypes) => {
  const limitsMessage = {
    [DaysLimitHitTypes.Daily]: remainingLimit
      ? TXTWCL_This_is_because_the_maximum_li_ELL
      : TXTWCL_This_is_because_you_can_only_d_ELL,
    [DaysLimitHitTypes.Weekly]: remainingLimit
      ? TXTWCL_This_is_because_the_maximum_li_ELL_2
      : TXTWCL_This_is_because_you_can_only_d_ELL_1,
    [DaysLimitHitTypes.Monthly]: remainingLimit
      ? TXTWCL_This_is_because_the_maximum_li_ELL_1
      : TXTWCL_This_is_because_you_can_only_d_ELL_2
  };

  return limitsMessage[daysLimitHitType];
};

const getLimits = ({ day, week, month }: MaxLimitData, daysLimitHitType: DaysLimitHitTypes) => {
  const limit = {
    [DaysLimitHitTypes.Daily]: day,
    [DaysLimitHitTypes.Weekly]: week,
    [DaysLimitHitTypes.Monthly]: month
  };

  return limit[daysLimitHitType];
};

export const createMaxLimitValidator =
  (
    balanceCurrency = Currency.USDollar,
    daysLimitHitType: DaysLimitHitTypes,
    depositLimitHitType: DepositLimitHitTypes,
    maxLimitData: MaxLimitData,
    rates: Rate[],
    remainingLimit: number,
    systemName: string
  ) =>
  (value: number, allValues: { deposit: { currency: Currency } }): Error => {
    const {
      deposit: { currency }
    } = allValues;

    const remainingLimitDollarAmount = remainingLimit / 100;
    const { transaction } = maxLimitData;
    const currencyRate = getConversionRate(rates, currency);
    const convertedAmount = convertAmountByRate(Number(value), currencyRate, currency);
    const isBalanceCurrencyZeroExponent = isZeroExponentCurrency(balanceCurrency);
    const isSelectedCurrencyZeroExponent = isZeroExponentCurrency(currency);

    const valid = () => {
      const isValidForSelfImposed =
        depositLimitHitType === DepositLimitHitTypes.SelfImposedLimit &&
        convertedAmount <= remainingLimitDollarAmount &&
        convertedAmount <= transaction;

      const isValidForPaysystem =
        depositLimitHitType !== DepositLimitHitTypes.SelfImposedLimit &&
        !(
          remainingLimitDollarAmount < transaction && convertedAmount > remainingLimitDollarAmount
        ) &&
        convertedAmount <= transaction;

      if (
        isNaN(value) ||
        (remainingLimitDollarAmount > 0 && (isValidForSelfImposed || isValidForPaysystem))
      ) {
        return true;
      }
      return false;
    };

    const message = () => {
      if (remainingLimitDollarAmount < transaction) {
        if (depositLimitHitType === DepositLimitHitTypes.SelfImposedLimit) {
          return [
            {
              values: {
                0: (
                  <CurrencyAmount
                    value={convertAmountByRate(
                      Number(remainingLimitDollarAmount),
                      currencyRate,
                      currency,
                      true
                    )}
                    isZeroExponentCurrency={isSelectedCurrencyZeroExponent}
                    currency={currency}
                    dollarValue
                  />
                )
              },
              ...TXTWCL_You_can_deposit_up_to_N0_befor_ELL_1
            },
            { ...TXTWCL__Please_enter_a_lower_amountX }
          ];
        }
        if (depositLimitHitType !== DepositLimitHitTypes.PaysystemLimit) {
          return {
            ...depositLimitMessages[depositLimitHitType],
            values: {
              0: (
                <CurrencyAmount
                  value={convertAmountByRate(
                    Number(remainingLimitDollarAmount),
                    currencyRate,
                    currency,
                    true
                  )}
                  isZeroExponentCurrency={isSelectedCurrencyZeroExponent}
                  currency={currency}
                  dollarValue
                />
              )
            }
          };
        }
        if (depositLimitHitType === DepositLimitHitTypes.PaysystemLimit) {
          if (remainingLimitDollarAmount === 0) {
            return [
              { ...TXTWCL_SorryX_your_deposit_request_ha_ELL },
              {
                ...setLimitsMessage(remainingLimitDollarAmount, daysLimitHitType),
                values: {
                  0: (
                    <CurrencyAmount
                      value={getLimits(maxLimitData, daysLimitHitType)}
                      isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
                      currency={balanceCurrency}
                      dollarValue
                    />
                  )
                }
              },
              { ...TXTWCL_You_have_now_reached_this_limi_ELL }
            ];
          }
          return [
            { ...TXTWCL_SorryX_your_deposit_request_ha_ELL },
            {
              ...setLimitsMessage(remainingLimitDollarAmount, daysLimitHitType),
              values: {
                0: (
                  <CurrencyAmount
                    value={getLimits(maxLimitData, daysLimitHitType)}
                    isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
                    currency={balanceCurrency}
                    dollarValue
                  />
                )
              }
            },
            {
              ...TXTWCL_You_can_only_deposit_another_N_ELL,
              values: {
                0: (
                  <CurrencyAmount
                    value={remainingLimitDollarAmount}
                    isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
                    currency={balanceCurrency}
                    dollarValue
                  />
                )
              }
            }
          ];
        }
      }

      return [
        { ...TXTWCL_SorryX_your_deposit_request_ha_ELL },
        {
          ...TXTWCL_Your_deposit_of_N0_is_more_tha_ELL,
          values: {
            0: (
              <CurrencyAmount
                value={value}
                isZeroExponentCurrency={isSelectedCurrencyZeroExponent}
                currency={currency}
                dollarValue
              />
            ),
            1: (
              <CurrencyAmount
                value={transaction}
                isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
                currency={balanceCurrency}
                dollarValue
              />
            ),
            2: systemName
          }
        },
        { ...TXTWCL_Please_choose_another_payment__ELL }
      ];
    };

    return { valid: valid(), message: message() };
  };
